/**
 * Unified Product Discovery Service
 *
 * PHASE 2.2: Enhanced iOS IAP Integration with Database Analytics
 *
 * This service provides a comprehensive unified interface for product discovery that leverages
 * existing Stripe products and maps them to iOS IAP products using the centralized
 * ProductConfigurationService. Enhanced in Phase 2.2 with database integration for
 * subscription analytics, transaction history, and cross-platform subscription management.
 *
 * CORE FEATURES:
 * - Unified product discovery across Stripe and iOS IAP platforms
 * - Platform-aware product filtering and mapping with availability detection
 * - Integration with existing Stripe product system and iOS IAP store products
 * - Centralized product configuration usage with pricing parity validation
 * - Real-time product availability checking across platforms
 *
 * PHASE 2.2 ENHANCEMENTS:
 * - Database integration for subscription history and analytics
 * - Cross-platform transaction tracking and correlation
 * - Active subscription detection across all platforms
 * - User-specific product recommendations based on purchase history
 * - Enhanced analytics and debugging capabilities
 * - Performance metrics and success rate tracking
 *
 * DATABASE INTEGRATION:
 * - Uses CrossPlatformTransactionService for transaction history
 * - Provides subscription status across all platforms
 * - Tracks user purchase patterns and preferences
 * - Supports analytics and business intelligence
 *
 * PLATFORM SUPPORT:
 * - iOS Native App: iOS IAP via cordova-plugin-purchase
 * - Android Native App: Stripe (Phase 6 will add Android IAP)
 * - Web Browser: Stripe checkout
 *
 * INTEGRATION POINTS:
 * - ProductConfigurationService: Centralized product mapping
 * - IOSInAppPurchaseService: iOS IAP product discovery
 * - CrossPlatformTransactionService: Database operations
 * - PaymentRoutingService: Payment method routing
 * - Unified Permissions System: User authentication context
 */

import { productConfigurationService, type ProductConfiguration } from '@/services/iap/ProductConfigurationService';
import { iosIAPService } from '@/services/iap/IOSInAppPurchaseService';
import { crossPlatformTransactionService } from '@/services/database/CrossPlatformTransactionService';
import { isIOSNativeApp } from '@/utils/platformUtils';
import type { CrossPlatformTransaction, SubscriptionPlatformMapping } from '@/types/crossPlatformTransactions';

/**
 * Unified product information combining Stripe and IAP data
 */
export interface UnifiedProduct {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly interval: 'monthly' | 'annual';
  readonly priceInCents: number;
  readonly formattedPrice: string;
  readonly currency: string;
  readonly isAvailable: boolean;
  readonly platform: 'stripe' | 'ios_iap';
  readonly stripeData?: {
    readonly priceId: string;
  };
  readonly iosData?: {
    readonly productId: string;
    readonly type: string;
    readonly storeProduct?: unknown; // Actual store product from cordova-plugin-purchase
  };
  readonly databaseIntegration?: {
    readonly hasActiveSubscription: boolean;
    readonly lastTransactionStatus?: string;
    readonly transactionCount: number;
  };
}

/**
 * Product discovery options
 */
export interface ProductDiscoveryOptions {
  readonly platform?: 'stripe' | 'ios_iap' | 'auto';
  readonly interval?: 'monthly' | 'annual';
  readonly includeUnavailable?: boolean;
}

/**
 * Product discovery result
 */
export interface ProductDiscoveryResult {
  readonly products: readonly UnifiedProduct[];
  readonly platform: 'stripe' | 'ios_iap';
  readonly isAvailable: boolean;
  readonly error?: string;
}

/**
 * Unified Product Discovery Service
 * Provides cross-platform product discovery with automatic platform detection
 */
export class UnifiedProductDiscoveryService {
  private readonly isIOSNative: boolean;
  private readonly productConfigurations: readonly ProductConfiguration[];
  private currentUserId?: string;

  constructor() {
    this.isIOSNative = isIOSNativeApp();
    this.productConfigurations = productConfigurationService.getAllProducts();

    console.log('🔍 Unified Product Discovery: Initialized', {
      platform: this.isIOSNative ? 'iOS' : 'web/android',
      configurationsCount: this.productConfigurations.length
    });
  }

  /**
   * Set current user ID for database integration
   */
  setCurrentUserId(userId: string): void {
    this.currentUserId = userId;
    console.log('👤 Unified Product Discovery: Current user ID set for database integration:', userId);
  }

  /**
   * Discover products based on platform and options
   */
  async discoverProducts(options: ProductDiscoveryOptions = {}): Promise<ProductDiscoveryResult> {
    const platform = this.determinePlatform(options.platform);
    
    console.log('🔍 Unified Product Discovery: Discovering products', {
      platform,
      options
    });

    try {
      if (platform === 'ios_iap') {
        return await this.discoverIOSProducts(options);
      } else {
        return await this.discoverStripeProducts(options);
      }
    } catch (error) {
      console.error('❌ Unified Product Discovery: Discovery failed:', error);
      return {
        products: [],
        platform,
        isAvailable: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get a specific product by ID
   */
  async getProduct(productId: string, options: ProductDiscoveryOptions = {}): Promise<UnifiedProduct | undefined> {
    const result = await this.discoverProducts(options);
    return result.products.find(product => 
      product.id === productId || 
      product.stripeData?.priceId === productId ||
      product.iosData?.productId === productId
    );
  }

  /**
   * Check if products are available on current platform
   */
  async isProductDiscoveryAvailable(): Promise<boolean> {
    const platform = this.determinePlatform();
    
    if (platform === 'ios_iap') {
      return iosIAPService.isAvailable();
    } else {
      // Stripe is always available for web/android
      return true;
    }
  }

  /**
   * Get platform-specific product identifier
   */
  getPlatformProductId(unifiedProductId: string, targetPlatform?: 'stripe' | 'ios_iap'): string | undefined {
    const platform = targetPlatform || this.determinePlatform();
    const config = this.productConfigurations.find(config => config.id === unifiedProductId);
    
    if (!config) {
      console.warn(`⚠️ Unified Product Discovery: Product configuration not found for ${unifiedProductId}`);
      return undefined;
    }

    if (platform === 'ios_iap') {
      return config.ios.productId;
    } else {
      return config.stripe.priceId;
    }
  }

  /**
   * Determine which platform to use for product discovery
   */
  private determinePlatform(requestedPlatform?: 'stripe' | 'ios_iap' | 'auto'): 'stripe' | 'ios_iap' {
    if (requestedPlatform === 'stripe') {
      return 'stripe';
    }
    
    if (requestedPlatform === 'ios_iap') {
      return 'ios_iap';
    }

    // Auto-detect based on platform and availability
    if (this.isIOSNative && iosIAPService.isAvailable()) {
      return 'ios_iap';
    }

    return 'stripe';
  }

  /**
   * Discover iOS IAP products
   */
  private async discoverIOSProducts(options: ProductDiscoveryOptions): Promise<ProductDiscoveryResult> {
    if (!iosIAPService.isAvailable()) {
      console.log('🚫 Unified Product Discovery: iOS IAP not available');
      return {
        products: [],
        platform: 'ios_iap',
        isAvailable: false,
        error: 'iOS IAP not available'
      };
    }

    const storeProducts = iosIAPService.getProducts();
    const unifiedProducts: UnifiedProduct[] = [];

    for (const config of this.productConfigurations) {
      // Filter by interval if specified
      if (options.interval && config.interval !== options.interval) {
        continue;
      }

      const storeProduct = storeProducts.find(p => p.id === config.ios.productId);
      const isAvailable = !!storeProduct;

      // Skip unavailable products unless explicitly requested
      if (!isAvailable && !options.includeUnavailable) {
        continue;
      }

      // Get database integration information
      const databaseIntegration = await this.getDatabaseIntegration(config.id);

      unifiedProducts.push({
        id: config.id,
        name: config.name,
        description: config.description,
        interval: config.interval,
        priceInCents: config.priceInCents,
        formattedPrice: (storeProduct as unknown as { raw?: { price?: string } })?.raw?.price ||
                       (storeProduct as unknown as { offers?: Array<{ pricingPhases?: Array<{ price?: string }> }> })?.offers?.[0]?.pricingPhases?.[0]?.price ||
                       config.formattedPrice,
        currency: (storeProduct as unknown as { currency?: string })?.currency || config.currency,
        isAvailable,
        platform: 'ios_iap',
        iosData: {
          productId: config.ios.productId,
          type: config.ios.type,
          storeProduct
        },
        databaseIntegration
      });
    }

    console.log(`✅ Unified Product Discovery: Found ${unifiedProducts.length} iOS products`);

    return {
      products: unifiedProducts,
      platform: 'ios_iap',
      isAvailable: true
    };
  }

  /**
   * Discover Stripe products
   */
  private async discoverStripeProducts(options: ProductDiscoveryOptions): Promise<ProductDiscoveryResult> {
    const unifiedProducts: UnifiedProduct[] = [];

    for (const config of this.productConfigurations) {
      // Filter by interval if specified
      if (options.interval && config.interval !== options.interval) {
        continue;
      }

      // Stripe products are always available (we assume Stripe is properly configured)
      const isAvailable = true;

      // Get database integration information
      const databaseIntegration = await this.getDatabaseIntegration(config.id);

      unifiedProducts.push({
        id: config.id,
        name: config.name,
        description: config.description,
        interval: config.interval,
        priceInCents: config.priceInCents,
        formattedPrice: config.formattedPrice,
        currency: config.currency,
        isAvailable,
        platform: 'stripe',
        stripeData: {
          priceId: config.stripe.priceId
        },
        databaseIntegration
      });
    }

    console.log(`✅ Unified Product Discovery: Found ${unifiedProducts.length} Stripe products`);

    return {
      products: unifiedProducts,
      platform: 'stripe',
      isAvailable: true
    };
  }

  /**
   * Get database integration information for a product
   *
   * This method enriches product discovery with user-specific database information,
   * providing insights into subscription status, transaction history, and purchase patterns.
   * This data is used for:
   * - Showing subscription status in product listings
   * - Providing purchase history context
   * - Supporting analytics and business intelligence
   * - Enabling personalized product recommendations
   *
   * @param productId - The unified product ID to get integration info for
   * @returns Database integration information including subscription status and transaction history
   */
  private async getDatabaseIntegration(productId: string): Promise<UnifiedProduct['databaseIntegration']> {
    // Return minimal data if no user is authenticated
    if (!this.currentUserId) {
      console.log('🔍 Unified Product Discovery: No user ID set, returning minimal database integration');
      return {
        hasActiveSubscription: false,
        transactionCount: 0
      };
    }

    try {
      console.log(`🔍 Unified Product Discovery: Getting database integration for product ${productId}`);

      // Check if user has an active subscription for this product
      // This helps determine if the user should see upgrade/downgrade options
      const activeSubscription = await crossPlatformTransactionService.getActiveSubscription(this.currentUserId);
      const hasActiveSubscription = activeSubscription?.product_id === productId && activeSubscription?.status === 'active';

      // Get comprehensive transaction history for analytics and context
      // We fetch more transactions to get accurate counts and patterns
      const transactions = await crossPlatformTransactionService.getUserTransactions(this.currentUserId, {
        limit: 100 // Increased limit for better analytics
      });

      // Filter transactions specific to this product for detailed analysis
      const productTransactions = transactions.filter(tx => tx.unified_product_id === productId);
      const lastTransaction = productTransactions[0]; // Most recent transaction (sorted by created_at desc)

      console.log(`✅ Unified Product Discovery: Database integration retrieved for ${productId}:`, {
        hasActiveSubscription,
        transactionCount: productTransactions.length,
        lastTransactionStatus: lastTransaction?.status
      });

      return {
        hasActiveSubscription,
        lastTransactionStatus: lastTransaction?.status,
        transactionCount: productTransactions.length
      };
    } catch (error) {
      console.error('❌ Unified Product Discovery: Failed to get database integration info:', error);

      // Return safe defaults on error to prevent product discovery failure
      return {
        hasActiveSubscription: false,
        transactionCount: 0
      };
    }
  }

  /**
   * Get user's subscription history for analytics
   */
  async getUserSubscriptionHistory(): Promise<readonly CrossPlatformTransaction[]> {
    if (!this.currentUserId) {
      console.warn('⚠️ Unified Product Discovery: Cannot get subscription history - no user ID set');
      return [];
    }

    try {
      console.log('📊 Unified Product Discovery: Getting user subscription history');
      const transactions = await crossPlatformTransactionService.getUserTransactions(this.currentUserId, {
        limit: 50
      });

      console.log(`✅ Unified Product Discovery: Retrieved ${transactions.length} transactions`);
      return transactions;
    } catch (error) {
      console.error('❌ Unified Product Discovery: Failed to get subscription history:', error);
      return [];
    }
  }

  /**
   * Get active subscription across all platforms
   */
  async getActiveSubscription(): Promise<SubscriptionPlatformMapping | null> {
    if (!this.currentUserId) {
      console.warn('⚠️ Unified Product Discovery: Cannot get active subscription - no user ID set');
      return null;
    }

    try {
      console.log('🔍 Unified Product Discovery: Getting active subscription');
      const subscription = await crossPlatformTransactionService.getActiveSubscription(this.currentUserId);

      if (subscription) {
        console.log('✅ Unified Product Discovery: Found active subscription:', {
          id: subscription.id,
          productId: subscription.product_id,
          platform: subscription.primary_platform,
          status: subscription.status
        });
      } else {
        console.log('📭 Unified Product Discovery: No active subscription found');
      }

      return subscription;
    } catch (error) {
      console.error('❌ Unified Product Discovery: Failed to get active subscription:', error);
      return null;
    }
  }

  /**
   * Get debug information
   */
  getDebugInfo(): Record<string, unknown> {
    return {
      isIOSNative: this.isIOSNative,
      configurationsCount: this.productConfigurations.length,
      iosIAPAvailable: iosIAPService.isAvailable(),
      currentUserId: this.currentUserId,
      databaseIntegration: {
        enabled: !!this.currentUserId,
        crossPlatformTransactionService: 'available'
      },
      productConfigurations: this.productConfigurations.map(config => ({
        id: config.id,
        name: config.name,
        interval: config.interval,
        price: config.formattedPrice,
        platforms: {
          stripe: config.stripe.priceId,
          ios: config.ios.productId
        }
      }))
    };
  }
}

// Export singleton instance
export const unifiedProductDiscoveryService = new UnifiedProductDiscoveryService();
